import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiMail, FiUser, FiMessageSquare, FiCheckCircle, FiLoader, FiAlertCircle, FiInfo } = FiIcons;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError('Please verify that you are not a bot.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await supabase.functions.invoke('contact-submit', {
        body: {
          ...formData,
          turnstileToken
        }
      });

      if (submitError) throw submitError;

      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('Submission error:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileKey((currentKey) => currentKey + 1);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-[0.3em] mb-6"
          >
            <SafeIcon icon={FiInfo} className="mr-2" /> Secure Communication
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-black text-slate-900 tracking-tighter mb-6"
          >
            Get in <span className="text-indigo-600">Touch</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-500 font-medium max-w-2xl mx-auto"
          >
            Have questions about SOHLocal? Our team is here to help you connect with your local baking community.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Info Side */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 space-y-8"
          >
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em] mb-8">Support Hub</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mr-4 text-indigo-600 flex-shrink-0">
                    <SafeIcon icon={FiMail} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email Protection</p>
                    <p className="text-sm font-bold text-slate-900 leading-relaxed">
                      Our support email is hidden from bots. All messages are routed through our secure server.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mr-4 text-emerald-600 flex-shrink-0">
                    <SafeIcon icon={FiCheckCircle} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Response Time</p>
                    <p className="text-sm font-bold text-slate-900">Typically within 24-48 hours.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl">
              <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em] mb-4">Pro Tip</h3>
              <p className="text-sm font-medium text-slate-300 leading-relaxed">
                If you have questions for a specific baker, please use the "Message" button on their profile for a faster response.
              </p>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3"
          >
            {success ? (
              <div className="bg-white p-12 rounded-[3rem] shadow-2xl border border-slate-100 text-center">
                <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto mb-8">
                  <SafeIcon icon={FiCheckCircle} className="text-4xl text-emerald-600" />
                </div>
                <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Message Received</h2>
                <p className="text-slate-500 font-medium mb-8">
                  Thank you for reaching out. We have securely received your inquiry and will get back to you shortly.
                </p>
                <button 
                  onClick={() => setSuccess(false)}
                  className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-[3rem] shadow-2xl border border-slate-100 space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-100 text-red-700 px-5 py-4 rounded-2xl flex items-center text-sm font-bold">
                    <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" />
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Your Name</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                        <SafeIcon icon={FiUser} className="text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                      </div>
                      <input 
                        type="text" 
                        name="name"
                        required 
                        value={formData.name}
                        onChange={handleChange}
                        className="pl-12 block w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-bold" 
                        placeholder="Alex Smith" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Email Address</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                        <SafeIcon icon={FiMail} className="text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                      </div>
                      <input 
                        type="email" 
                        name="email"
                        required 
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-12 block w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-bold" 
                        placeholder="alex@example.com" 
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Subject</label>
                  <input 
                    type="text" 
                    name="subject"
                    required 
                    value={formData.subject}
                    onChange={handleChange}
                    className="block w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-bold" 
                    placeholder="How can we help?" 
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Message</label>
                  <div className="relative group">
                    <div className="absolute top-4 left-5 pointer-events-none">
                      <SafeIcon icon={FiMessageSquare} className="text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                    </div>
                    <textarea 
                      name="message"
                      required 
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="pl-12 block w-full px-5 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-medium resize-none leading-relaxed" 
                      placeholder="Write your message here..." 
                    />
                  </div>
                </div>

                <div className="flex justify-center py-4">
                  <Turnstile 
                    key={turnstileKey}
                    siteKey={SITE_KEY} 
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={loading || !turnstileToken}
                  className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 disabled:opacity-50 transform active:scale-[0.98] flex items-center justify-center"
                >
                  {loading ? (
                    <><SafeIcon icon={FiLoader} className="animate-spin mr-3" /> Sending...</>
                  ) : (
                    <><SafeIcon icon={FiSend} className="mr-3" /> Send Secure Message</>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
